// Collapsible thinking/reasoning block shown above assistant message content.
// Extracted from AgentChat.tsx (was inline there) to enable reuse.
// Generic — no Platinum-specific dependencies.
// See ../../docs/09-frontend-components.md.

import React, { useState, useRef } from 'react'
import { Box, Typography, Collapse } from '@mui/material'
import { CARD_SX } from './surface.js'
import { collapseBase64 } from '../base64.js'
import ActivityOrb from './ActivityOrb.js'

interface ThinkingBlockProps {
  content: string
  isActivelyThinking?: boolean
}

/** Collapsible thinking/reasoning block shown above assistant message content.
 * Closed by default even while actively thinking — the live text streams in
 * the activity status line instead (see AgentChat's status area), so this
 * block's job is just the after-the-fact "Reasoning" disclosure. Reader can
 * still expand it manually mid-stream to follow along. */
export default function ThinkingBlock({ content, isActivelyThinking }: ThinkingBlockProps) {
  const [expanded, setExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const isOpen = expanded

  // Reasoning that quotes an HTML file quotes its inlined images too; show the
  // size of each, not the bytes (#1347 item 13).
  const shown = collapseBase64(content)

  // Single-line preview: strip newlines and truncate
  const preview = shown.replace(/\n/g, ' ').trim()
  const previewTruncated = preview.length > 100 ? preview.slice(0, 100) + '...' : preview

  // Auto-scroll to bottom while actively thinking
  if (isActivelyThinking && contentRef.current) {
    const el = contentRef.current
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    if (isNearBottom) {
      el.scrollTop = el.scrollHeight
    }
  }

  return (
    <Box
      data-testid="thinking-block"
      sx={{
        ...CARD_SX,
        maxWidth: '80%',
        mb: 0.5,
      }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          px: '12px',
          py: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        {isActivelyThinking && (
          <ActivityOrb status={{ category: 'thinking' }} label="Reasoning" style={{ marginRight: 6 }} />
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: 11,
            flexShrink: 0,
            ...(isActivelyThinking ? {
              background: 'linear-gradient(90deg, rgba(0,178,255,0.5) 0%, #00B2FF 50%, rgba(0,178,255,0.5) 100%)',
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              animation: 'thinkingShimmer 1.6s linear infinite',
              '@keyframes thinkingShimmer': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', color: '#00B2FF', background: 'none' },
            } : {
              color: '#00B2FF',
            }),
          }}
        >
          {isActivelyThinking ? 'Reasoning...' : 'Reasoning'}
        </Typography>
        {!isOpen && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: 11,
              mx: '6px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            | {previewTruncated}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 13, ml: 'auto', flexShrink: 0, pl: '8px' }}>
          {expanded ? '−' : '+'}
        </Typography>
      </Box>
      <Collapse in={isOpen}>
        <Box
          ref={contentRef}
          sx={{
            px: '10px',
            pb: '8px',
            fontSize: 12,
            color: 'text.secondary',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            ...(isActivelyThinking ? { maxHeight: 200, overflowY: 'auto' } : {}),
          }}
        >
          {shown}
        </Box>
      </Collapse>
    </Box>
  )
}
